import { Divider, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FaceNeutralIcon } from 'assets/svg';
import { useAppSelector } from 'features/app/hooks';
import { isCallTakenTimeExistSelector } from 'features/call/call-base/store/selectors';
import { BugReportCommentType } from 'features/call/call-comments/enums';
import {
  VrsCallCommentsBasePopup,
  VrsCallCommentsBugReportCallId,
  VrsCallCommentsBugReportCallTakenTime,
  VrsCallCommentsBugReportReasonControl,
  VrsCallCommentsBugReportTextareaControl,
  VrsCallCommentsCategoriesControl,
  VrsCallCommentsFormButtons,
} from 'features/call/vrs-call-comments/components';
import { BugReportReasonOption } from 'features/call/vrs-call-comments/constants';
import { BugReportReason } from 'features/call/vrs-call-comments/enums';
import { useVrsCallCommentsFormSubmit } from 'features/call/vrs-call-comments/hooks';
import type { VrsCallCommentsBugReportForm } from 'features/call/vrs-call-comments/interfaces';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';

export const VrsCallCommentsBugReport = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'bugReport',
  });
  const [categories, setCategories] = useState(new Set(''));
  const { control, handleSubmit } = useForm<VrsCallCommentsBugReportForm>({
    mode: 'onChange',
    defaultValues: {
      video: null,
      audio: null,
      callerText: null,
      emergency: null,
      dialpad: null,
      teaming: null,
      handoff: null,
    },
  });
  const { isValid } = useFormState({ control });
  const bugReportReasonControl = useMemo(
    () =>
      Object.values(BugReportReason)
        .filter((reason: BugReportReason) => reason !== BugReportReason.OTHER)
        .map(
          (reason: BugReportReason) =>
            categories.has(reason) && (
              <VrsCallCommentsBugReportReasonControl
                control={control}
                key={reason}
                controlName={reason}
                reasons={BugReportReasonOption[reason]}
                reasonTitle={t(`${reason}ReasonTitle`)}
              />
            )
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories, control]
  );
  const isCallTakenTimeExist = useAppSelector(isCallTakenTimeExistSelector);
  const { submitCallComments } = useVrsCallCommentsFormSubmit();

  const submitForm = async (formData: VrsCallCommentsBugReportForm) => {
    const commentTypes = formData.categories.map(
      (category: BugReportReason) =>
        formData[category] || BugReportCommentType.BROTHERB
    );

    await submitCallComments({
      commentTypes,
      commentText: formData.commentText,
    });
  };

  return (
    <VrsCallCommentsBasePopup
      icon={<FaceNeutralStyledIcon title='face-neutral-icon' />}
      title={t('title')}
      subtitle={t('subtitle')}
      containerTitle={t('containerTitle')}
    >
      <StyledForm onSubmit={handleSubmit(submitForm)}>
        <ScrollableContainer>
          <VrsCallCommentsBugReportCallId />
          {isCallTakenTimeExist && <VrsCallCommentsBugReportCallTakenTime />}
          <VrsCallCommentsCategoriesControl
            changeCategoryHandler={setCategories}
            control={control}
          />
          {bugReportReasonControl}
          {Boolean(categories.size) && (
            <VrsCallCommentsBugReportTextareaControl
              control={control}
              isRequired={categories.has(BugReportReason.OTHER)}
            />
          )}
          <Separator title='bug-report-form-divider' />
          <Typography
            variant='subtitle2'
            color={theme.palette.grey[300]}
            mt={12}
          >
            {t('description')}
          </Typography>
        </ScrollableContainer>
        <VrsCallCommentsFormButtons isValid={isValid} />
      </StyledForm>
    </VrsCallCommentsBasePopup>
  );
};

const StyledForm = styled('form')`
  margin-top: ${theme.spacing(2)};
`;

const ScrollableContainer = styled('div')`
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const FaceNeutralStyledIcon = styled(FaceNeutralIcon)`
  height: 60px;
  width: 60px;
`;

const Separator = styled(Divider)`
  border-color: ${theme.palette.grey[600]};
  margin: ${theme.spacing(8, 0)};
`;
